import { ActionIcon, Box, Button, Group, NumberInput, Text, TextInput } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import type { StepRuntimeOptionsValue } from "@models/doctype";
import { useTranslation } from "@hooks/useTranslation";
import { OptionSections, setPath, stepRuntimeSections } from "./RuntimeOptions";

interface StepRuntimeOptionsFieldsProps {
    /** Chunking exists on PAGE_DETECTION only, the missing-domain policy on DATA_EXTRACTION. */
    stepType: string;
    value: StepRuntimeOptionsValue | null;
    onChange: (value: StepRuntimeOptionsValue | null) => void;
    onDirty?: () => void;
}

const PAGE_CAPS_PATH = ["chunkingOptions", "max_pages_per_domain"];

export const StepRuntimeOptionsFields = ({
    stepType,
    value,
    onChange,
    onDirty,
}: StepRuntimeOptionsFieldsProps) => {
    const { t } = useTranslation();
    const publish = (next: StepRuntimeOptionsValue | null) => {
        onChange(next);
        onDirty?.();
    };

    const pageCaps = value?.chunkingOptions?.max_pages_per_domain || {};

    const updatePageCap = (oldDomain: string, domain: string, pages: string | number) => {
        const nextCaps = { ...pageCaps };
        delete nextCaps[oldDomain];
        if (domain.trim() && pages !== "") nextCaps[domain.trim()] = Number(pages);
        publish(setPath(value, PAGE_CAPS_PATH, Object.keys(nextCaps).length ? nextCaps : undefined));
    };

    // Per-domain caps are a map, not a fixed field, so they render beside the chunking specs.
    const pageCapsEditor = (
        <Box mt="md">
            <Text size="sm" fw={500}>{t("admin.flowConfigs.options.pageCaps.label")}</Text>
            <Text size="xs" c="dimmed" mb="xs">{t("admin.flowConfigs.options.pageCaps.hint")}</Text>
            {Object.entries(pageCaps).map(([domain, pages]) => (
                <Group key={domain} grow align="flex-end" mb="xs">
                    <TextInput
                        label={t("admin.flowConfigs.options.pageCaps.domain")}
                        value={domain}
                        onChange={(event) => updatePageCap(domain, event.currentTarget.value, pages)}
                    />
                    <NumberInput
                        label={t("admin.flowConfigs.options.pageCaps.maxPages")}
                        value={pages}
                        min={0}
                        onChange={(next) => updatePageCap(domain, domain, next)}
                    />
                    <ActionIcon
                        aria-label={t("admin.flowConfigs.options.pageCaps.remove", { domain })}
                        color="red"
                        variant="subtle"
                        onClick={() => updatePageCap(domain, "", "")}
                    >
                        <IconTrash size={16} />
                    </ActionIcon>
                </Group>
            ))}
            <Button
                variant="light"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => {
                    let index = 1;
                    let domain = `domain_${index}`;
                    while (domain in pageCaps) domain = `domain_${++index}`;
                    updatePageCap("", domain, 0);
                }}
            >
                {t("admin.flowConfigs.options.pageCaps.add")}
            </Button>
        </Box>
    );

    return (
        <OptionSections
            sections={stepRuntimeSections(stepType)}
            value={value}
            onChange={publish}
            renderExtra={(sectionId) => (sectionId === "chunking" ? pageCapsEditor : null)}
            extraOverrides={(sectionId) => (sectionId === "chunking" ? Object.keys(pageCaps).length : 0)}
        />
    );
};
